import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Redirect } from "react-router-dom";
import Axios from "axios";
import {
  FaBus,
  FaUserGraduate,
  FaSearch,
  FaSave,
  FaArrowLeft,
  FaUser,
  FaCheck,
} from "react-icons/fa";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";
import COLORS from "../resources/colors/COLORS";
import MdlSelectSingleServiceStudent from "../dialog/MdlSelectSingleServiceStudent";
import SessionManager from "../Utils/SessionManager";
import BackButton from "../ChildComponent/BackButton";
import myToast from "../Utils/MyToast";
import MyLoader from "../Spinner/MyLoader";

const API_BASE = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;
const SERVICE_ID = "SRV001";
const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const MONTH_NAME_TO_NUMBER = {
  Jan: "01",
  Feb: "02",
  Mar: "03",
  Apr: "04",
  May: "05",
  Jun: "06",
  Jul: "07",
  Aug: "08",
  Sep: "09",
  Oct: "10",
  Nov: "11",
  Dec: "12",
};

function parseSessionStartDate(raw) {
  if (!raw) return null;
  const value = String(raw).trim();

  let m = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) {
    return new Date(+m[1], +m[2] - 1, +m[3]);
  }

  m = value.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
  if (m) {
    return new Date(+m[3], +m[2] - 1, +m[1]);
  }

  const fallback = new Date(value);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

function defaultAcademicStart() {
  const now = new Date();
  const year = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return new Date(year, 3, 1);
}

function resolveSessionStartDate(preferred) {
  const candidates = [
    preferred,
    typeof localStorage !== "undefined" ? localStorage.getItem("START_DATE") : null,
    SessionManager.getCurrentSession()?.[0]?.start_date,
  ];
  for (let i = 0; i < candidates.length; i++) {
    const parsed = parseSessionStartDate(candidates[i]);
    if (parsed) return parsed;
  }
  return defaultAcademicStart();
}

function buildSessionMonths(preferred) {
  const cal = resolveSessionStartDate(preferred);
  const y0 = cal.getFullYear();
  const m0 = cal.getMonth();
  const months = new Array(12);
  for (let i = 0; i < 12; i++) {
    const d = new Date(y0, m0 + i, 1);
    months[i] = `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
  }
  return months;
}

function reverseApplicableMonths(monthList) {
  if (!monthList?.length) return "";
  const out = [];
  for (let i = 0; i < monthList.length; i++) {
    const [monthName, year] = String(monthList[i]).split(" ");
    const month = MONTH_NAME_TO_NUMBER[monthName];
    if (month && year) out.push(`${year}-${month}`);
  }
  return out.join(",");
}

const HOURS_12 = Array.from({ length: 12 }, (_, i) => String(i + 1));
const MINUTES_60 = Array.from({ length: 60 }, (_, i) =>
  String(i).padStart(2, "0")
);

/** Convert 12h UI parts → API string (24h, matches Android storage) */
function toApiPickupTime(hour12, minute, ampm) {
  if (!hour12 || minute === "" || minute == null || !ampm) return "";
  let h = parseInt(hour12, 10);
  if (Number.isNaN(h) || h < 1 || h > 12) return "";
  const m = String(minute).padStart(2, "0");
  if (ampm === "AM") {
    if (h === 12) h = 0;
  } else if (ampm === "PM") {
    if (h !== 12) h += 12;
  } else {
    return "";
  }
  return `${h}:${m}`;
}

const INITIAL_FORM = {
  routeId: "",
  vehicleId: "",
  pickupPoint: "",
  pickupPointId: "",
  pickupHour: "",
  pickupMinute: "",
  pickupAmPm: "AM",
  transportFee: "",
};

const AddTransportStudent = () => {
  const loggedIn = localStorage.getItem("token") !== null;
  const abortRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);
  const [serviceMonth, setServiceMonth] = useState([]);
  const [studentId, setStudentId] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [mStudentDetails, setSelectedStudent] = useState(null);
  const [mSessionMonth, setSessionMonth] = useState([]);
  const [mRoutesList, setRoutesList] = useState([]);
  const [mVehicleList, setVehicleList] = useState([]);
  const [mAllPickupPoint, setAllPickupPoint] = useState([]);

  const {
    routeId,
    vehicleId,
    pickupPoint,
    pickupPointId,
    pickupHour,
    pickupMinute,
    pickupAmPm,
    transportFee,
  } = form;

  const pickupTime = useMemo(
    () => toApiPickupTime(pickupHour, pickupMinute, pickupAmPm),
    [pickupHour, pickupMinute, pickupAmPm]
  );

  const pickupByName = useMemo(() => {
    const map = new Map();
    for (let i = 0; i < mAllPickupPoint.length; i++) {
      const point = mAllPickupPoint[i];
      const name = String(point.pickup_point_name || "")
        .trim()
        .toLowerCase();
      if (name) map.set(name, point.pickup_point_id || "");
    }
    return map;
  }, [mAllPickupPoint]);

  const selectedMonthSet = useMemo(() => new Set(serviceMonth), [serviceMonth]);
  const allMonthsSelected =
    mSessionMonth.length > 0 && serviceMonth.length === mSessionMonth.length;

  const classLabel = useMemo(() => {
    const c = mStudentDetails?.class?.[0];
    if (!c) return "—";
    return [c.class_name, c.section_name].filter(Boolean).join(" · ") || "—";
  }, [mStudentDetails]);

  const rollNo =
    mStudentDetails?.roll_number ||
    mStudentDetails?.class?.[0]?.student_roll_no ||
    "—";

  const getAllSessionMonth = useCallback((sessionStartDate) => {
    setSessionMonth(buildSessionMonths(sessionStartDate));
    setServiceMonth([]);
  }, []);

  const getTransportSetupData = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) {
      setLoading(false);
      myToast.notify_warning("School code is missing.");
      return;
    }

    try {
      const response = await Axios.get(`${API_BASE}getTransportSetupData.php`, {
        params: { school_code: schoolCode },
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;

      if (response.data?.api_status === "OK") {
        const data = response.data.data || {};
        setRoutesList(data.routes || []);
        setAllPickupPoint(data.PickupPoints || []);
        setVehicleList(data.VehicleList || []);
      } else {
        setRoutesList([]);
        setAllPickupPoint([]);
        setVehicleList([]);
        myToast.notify_warning(
          response.data?.api_remark || "Could not load transport setup."
        );
      }
    } catch (error) {
      if (Axios.isCancel?.(error) || error?.name === "CanceledError" || error?.code === "ERR_CANCELED") {
        return;
      }
      console.error(error);
      myToast.notify_warning("Network error loading transport setup.");
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    getTransportSetupData();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [getTransportSetupData]);

  const handleClose = useCallback(() => setIsOpen(false), []);

  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      if (name === "pickupPoint") {
        const id =
          pickupByName.get(String(value || "").trim().toLowerCase()) || "";
        setForm((prev) => ({
          ...prev,
          pickupPoint: value,
          pickupPointId: id,
        }));
        return;
      }

      const keyMap = {
        route: "routeId",
        vehicle: "vehicleId",
        pickupHour: "pickupHour",
        pickupMinute: "pickupMinute",
        pickupAmPm: "pickupAmPm",
        transportFee: "transportFee",
      };
      const key = keyMap[name];
      if (!key) return;
      setForm((prev) => ({ ...prev, [key]: value }));
    },
    [pickupByName]
  );

  const handleSelectedStudent = useCallback(
    (selectedStudentId, selectedItem, selectedSessionId, _sessionName, sessionStartDate) => {
      setIsOpen(false);
      setSelectedStudent(selectedItem);
      setStudentId(selectedStudentId);
      setSessionId(selectedSessionId);
      getAllSessionMonth(sessionStartDate);
    },
    [getAllSessionMonth]
  );

  const handleMonthClick = useCallback((month) => {
    setServiceMonth((prev) =>
      prev.includes(month) ? prev.filter((m) => m !== month) : [...prev, month]
    );
  }, []);

  const toggleAllMonths = useCallback(() => {
    setServiceMonth((prev) =>
      prev.length === mSessionMonth.length ? [] : mSessionMonth.slice()
    );
  }, [mSessionMonth]);

  const resetForm = useCallback(() => {
    setForm(INITIAL_FORM);
    setServiceMonth([]);
    setSessionMonth([]);
    setStudentId("");
    setSessionId("");
    setSelectedStudent(null);
  }, []);

  const isValidInput = useCallback(() => {
    const checks = [
      [studentId, "Select a student"],
      [routeId, "Select a route"],
      [vehicleId, "Select a vehicle"],
      [String(pickupPoint || "").trim(), "Enter pickup point"],
      [pickupTime, "Select pickup time"],
      [String(transportFee || "").trim(), "Enter transport fee"],
      [serviceMonth.length, "Select at least one service month"],
    ];
    for (let i = 0; i < checks.length; i++) {
      if (!checks[i][0]) {
        myToast.notify_warning(checks[i][1]);
        return false;
      }
    }
    return true;
  }, [
    studentId,
    routeId,
    vehicleId,
    pickupPoint,
    pickupTime,
    transportFee,
    serviceMonth.length,
  ]);

  const handleSubmit = useCallback(async () => {
    if (!isValidInput()) return;

    const schoolCode = SessionManager.getSchoolCode();
    const staffId = SessionManager.getStaffDetails()?.staff_id;
    if (!schoolCode || !staffId) {
      myToast.notify_warning("Staff or school details missing");
      return;
    }

    setSaving(true);
    try {
      const cls = mStudentDetails?.class?.[0];
      const resolvedPickupPointId =
        pickupPointId ||
        pickupByName.get(String(pickupPoint || "").trim().toLowerCase()) ||
        "";

      const params = new URLSearchParams({
        staff_id: staffId,
        student_id: studentId,
        session_id: sessionId,
        class_id: cls?.class_id || "",
        section_id: cls?.section_id || "",
        vehicle_id: vehicleId,
        route_id: routeId,
        pickup_point_id: resolvedPickupPointId,
        pickup_point_name: pickupPoint,
        pickup_time: pickupTime,
        transport_fee: transportFee,
        concession_granted: "No",
        applicable_month: reverseApplicableMonths(serviceMonth),
        service_id: SERVICE_ID,
        school_code: schoolCode,
      });

      const response = await Axios.post(
        `${API_BASE}addTransportStudent.php`,
        params,
        {
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
        }
      );

      if (response.data?.api_status === "OK") {
        myToast.notify_success("Added successfully!");
        resetForm();
      } else {
        myToast.notify_warning(
          response.data?.api_remark || "Failed to add transport."
        );
      }
    } catch (error) {
      console.error("Error adding transport student:", error);
      myToast.notify_error("Failed to add transport student");
    } finally {
      setSaving(false);
    }
  }, [
    isValidInput,
    pickupPointId,
    pickupByName,
    pickupPoint,
    studentId,
    sessionId,
    mStudentDetails,
    vehicleId,
    routeId,
    pickupTime,
    transportFee,
    serviceMonth,
    resetForm,
  ]);

  if (!loggedIn) {
    return <Redirect to="/Login" />;
  }

  return (
    <div
      className="content payroll-page sm-page sr-page ats-page"
      style={{
        background: COLORS.white,
        fontFamily:
          'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}> Add Transport Student</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaBus />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Add Transport Student</h1>
              <p className="sm-hero__sub">
                Assign route, vehicle, pickup, fee, and service months
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            <Link to="/AdminMain/TransportStudent" className="ats-hero-link">
              <FaArrowLeft aria-hidden /> Students
            </Link>
          </div>
        </header>

        {loading ? (
          <div className="ats-loading">
            <MyLoader />
          </div>
        ) : (
          <div className="sr-body ats-body">
            <section className="sr-section ats-section ats-section--student">
              <div className="sr-section__head">
                <div className="sr-section__icon" aria-hidden>
                  <FaUserGraduate />
                </div>
                <div className="min-w-0">
                  <h2 className="sr-section__title">Student</h2>
                  <p className="sr-section__sub">
                    {mStudentDetails
                      ? "Selected student details"
                      : "Select a student to continue"}
                  </p>
                </div>
                <button
                  type="button"
                  className="sr-btn sr-btn--primary ats-select-btn"
                  onClick={() => setIsOpen(true)}
                >
                  <FaSearch aria-hidden />
                  {mStudentDetails ? "Change Student" : "Select Student"}
                </button>
              </div>
              <div className="sr-section__body">
                {mStudentDetails ? (
                  <div className="ats-student">
                    <div className="ats-student__avatar" aria-hidden>
                      {mStudentDetails.student_img_path ? (
                        <img
                          src={mStudentDetails.student_img_path}
                          alt=""
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      ) : (
                        <FaUser />
                      )}
                    </div>
                    <div className="ats-student__info min-w-0">
                      <h3 className="ats-student__name">
                        {mStudentDetails.stu_name || "Name"}
                        {mStudentDetails.father_name
                          ? ` S/o ${mStudentDetails.father_name}`
                          : ""}
                      </h3>
                      <div className="ats-student__pills">
                        <span className="ats-pill">Roll: {rollNo}</span>
                        <span className="ats-pill">Class: {classLabel}</span>
                        <span className="ats-pill">
                          {mStudentDetails.gender || "—"}
                        </span>
                        {mStudentDetails.student_enrollment_no ? (
                          <span className="ats-pill">
                            Enroll: {mStudentDetails.student_enrollment_no}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="ats-student-empty">
                    <div className="ats-student-empty__icon" aria-hidden>
                      <FaUserGraduate />
                    </div>
                    <p>No student selected yet</p>
                  </div>
                )}
              </div>
            </section>

            <section className="sr-section ats-section ats-section--transport">
              <div className="sr-section__head">
                <div className="sr-section__icon" aria-hidden>
                  <FaBus />
                </div>
                <div className="min-w-0">
                  <h2 className="sr-section__title">Transport details</h2>
                  <p className="sr-section__sub">
                    Configure route, vehicle, and pickup
                  </p>
                </div>
              </div>
              <div className="sr-section__body">
                <div className="sr-grid sr-grid--2">
                  <div className="sr-field">
                    <label className="sr-field__label" htmlFor="ats-route">
                      Route <span className="sr-field__req">*</span>
                    </label>
                    <select
                      id="ats-route"
                      name="route"
                      className="form-control sr-input"
                      value={routeId}
                      onChange={handleChange}
                    >
                      <option value="">Select route</option>
                      {mRoutesList.map((item) => (
                        <option key={item.route_id} value={item.route_id}>
                          {item.route_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="sr-field">
                    <label className="sr-field__label" htmlFor="ats-vehicle">
                      Vehicle <span className="sr-field__req">*</span>
                    </label>
                    <select
                      id="ats-vehicle"
                      name="vehicle"
                      className="form-control sr-input"
                      value={vehicleId}
                      onChange={handleChange}
                    >
                      <option value="">Select vehicle</option>
                      {mVehicleList.map((item) => (
                        <option key={item.vehicle_id} value={item.vehicle_id}>
                          {item.vehicle_name || item.vehicle_number} (
                          {item.vehicle_type || "—"})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="sr-field">
                    <label className="sr-field__label" htmlFor="ats-pickup">
                      Pickup point <span className="sr-field__req">*</span>
                    </label>
                    <input
                      id="ats-pickup"
                      name="pickupPoint"
                      className="form-control sr-input"
                      list="ats-pickup-list"
                      value={pickupPoint}
                      onChange={handleChange}
                      placeholder="Type or select pickup point"
                      autoComplete="off"
                    />
                    <datalist id="ats-pickup-list">
                      {mAllPickupPoint.map((point) => (
                        <option
                          key={point.pickup_point_id}
                          value={point.pickup_point_name}
                        />
                      ))}
                    </datalist>
                  </div>

                  <div className="sr-field">
                    <label className="sr-field__label" htmlFor="ats-hour">
                      Pickup time <span className="sr-field__req">*</span>
                    </label>
                    <div className="ats-time-12" role="group" aria-label="Pickup time">
                      <select
                        id="ats-hour"
                        name="pickupHour"
                        className="form-control sr-input ats-time-12__hour"
                        value={pickupHour}
                        onChange={handleChange}
                        aria-label="Hour"
                      >
                        <option value="">Hour</option>
                        {HOURS_12.map((h) => (
                          <option key={h} value={h}>
                            {h}
                          </option>
                        ))}
                      </select>
                      <span className="ats-time-12__sep" aria-hidden>
                        :
                      </span>
                      <select
                        id="ats-minute"
                        name="pickupMinute"
                        className="form-control sr-input ats-time-12__min"
                        value={pickupMinute}
                        onChange={handleChange}
                        aria-label="Minute"
                      >
                        <option value="">Min</option>
                        {MINUTES_60.map((m) => (
                          <option key={m} value={m}>
                            {m}
                          </option>
                        ))}
                      </select>
                      <select
                        id="ats-ampm"
                        name="pickupAmPm"
                        className="form-control sr-input ats-time-12__ampm"
                        value={pickupAmPm}
                        onChange={handleChange}
                        aria-label="AM or PM"
                      >
                        <option value="AM">AM</option>
                        <option value="PM">PM</option>
                      </select>
                    </div>
                    <p className="sr-field__hint">Hour · Minute · AM/PM</p>
                  </div>
                </div>
              </div>
            </section>

            <section className="sr-section ats-section ats-section--fee ats-fee-section">
              <div className="sr-section__head">
                <div className="sr-section__icon" aria-hidden>
                  <i className="fa fa-inr" />
                </div>
                <div className="min-w-0">
                  <h2 className="sr-section__title">
                    Transport fee & service months
                  </h2>
                  <p className="sr-section__sub">
                    {mSessionMonth.length
                      ? `${serviceMonth.length} of ${mSessionMonth.length} months selected`
                      : "Set fee and choose applicable service months"}
                  </p>
                </div>
              </div>
              <div className="sr-section__body">
                <div className="ats-fee-months">
                  <div className="ats-fee-box">
                    <label className="sr-field__label" htmlFor="ats-fee">
                      Transport fee <span className="sr-field__req">*</span>
                    </label>
                    <div className="ats-fee-input-wrap">
                      <span className="ats-fee-prefix" aria-hidden>
                        ₹
                      </span>
                      <input
                        id="ats-fee"
                        name="transportFee"
                        type="number"
                        min="0"
                        className="form-control sr-input ats-fee-input"
                        value={transportFee}
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </div>
                    <p className="sr-field__hint">
                      Monthly / applicable fee amount
                    </p>
                  </div>

                  <div className="ats-months-box">
                    <div className="ats-months-box__head">
                      <span className="sr-field__label">
                        Service months <span className="sr-field__req">*</span>
                      </span>
                      {mSessionMonth.length > 0 ? (
                        <button
                          type="button"
                          className="ats-months-toggle"
                          onClick={toggleAllMonths}
                        >
                          {allMonthsSelected ? "Clear all" : "Select all"}
                        </button>
                      ) : null}
                    </div>
                    {mSessionMonth.length === 0 ? (
                      <div className="ats-months-empty">
                        Select a student to choose applicable months.
                      </div>
                    ) : (
                      <div
                        className="ats-months"
                        role="group"
                        aria-label="Service months"
                      >
                        {mSessionMonth.map((month) => {
                          const active = selectedMonthSet.has(month);
                          return (
                            <button
                              key={month}
                              type="button"
                              className={`ats-month${active ? " is-active" : ""}`}
                              onClick={() => handleMonthClick(month)}
                            >
                              {active ? <FaCheck aria-hidden /> : null}
                              {month}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>

            <div className="ats-actions">
              <Link
                to="/AdminMain/TransportStudent"
                className="sr-btn sr-btn--ghost ats-btn-cancel"
              >
                Cancel
              </Link>
              <button
                type="button"
                className="sr-btn sr-btn--primary ats-btn-submit"
                onClick={handleSubmit}
                disabled={saving}
              >
                <FaSave aria-hidden />
                {saving ? "Saving…" : "Submit Transport Details"}
              </button>
            </div>
          </div>
        )}
      </div>

      {isOpen ? (
        <MdlSelectSingleServiceStudent
          actionClose={handleClose}
          actionDone={handleSelectedStudent}
          serviceId={SERVICE_ID}
        />
      ) : null}
    </div>
  );
};

export default AddTransportStudent;
